/**
 * Las palabras al día, en Ajustes del panel (encargo 0006, 24-oct-2026). Con ellas el tablero de la novela calcula cuánto
 * te falta: las de este libro, si le pones unas; si no, las de todos tus libros; si no, 1.500.
 *
 * No le pregunta nada a Word: la carpeta y el documento ya los sabe el panel. Lee la cifra al abrir Ajustes y cuando
 * cambia el documento; guarda solo al pulsar. Las frases son las de la pantalla grande (`pantalla/tableroLogica`).
 */
import { useEffect, useState } from "react";
import { ritmoDe, guardarRitmo } from "./guionApi";
import { deDondeEsElRitmo, fraseDeRitmoGuardado, leerPalabras, miles, rotuloParaTodos, RITMO_NO_VALIDO, type RitmoPrevisto } from "./pantalla/tableroLogica";

export default function Ritmo({ dir, doc }: { dir: string; doc: string | null }) {
  const [ritmo, setRitmo] = useState<RitmoPrevisto | null>(null);
  const [sinLeer, setSinLeer] = useState<string | null>(null);
  const [texto, setTexto] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [dicho, setDicho] = useState<{ bien: boolean; texto: string } | null>(null);

  useEffect(() => {
    setRitmo(null); setSinLeer(null); setDicho(null);
    let vivo = true;
    ritmoDe(dir || null, doc).then((r) => { if (vivo) setRitmo(r); }, (e) => { if (vivo) setSinLeer(String((e as Error)?.message ?? e)); });
    return () => { vivo = false; };
  }, [dir, doc]);

  /** Guarda la cifra, para este libro o para todos los tuyos. Solo al pulsar; lo que conteste el motor sale junto a los botones. */
  const guardar = async (cual: "libro" | "autor", quitar = false) => {
    const n = quitar ? null : leerPalabras(texto);
    if (!quitar && n == null) { setDicho({ bien: false, texto: RITMO_NO_VALIDO }); return; }
    setOcupado(true); setDicho(null);
    try {
      const r = await guardarRitmo(dir || null, doc, cual, n);
      setRitmo(r); setTexto("");
      setDicho({ bien: true, texto: fraseDeRitmoGuardado(cual, n, r) });
    } catch (e) {
      setDicho({ bien: false, texto: String((e as Error)?.message ?? e) });
    } finally { setOcupado(false); }
  };

  // A un libro se le pone su cifra si el panel sabe su carpeta y el libro tiene guion (sin guion no hay tablero que la use).
  const conLibro = !!dir && !!ritmo?.hay_guion;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Palabras al día</div>
      {ritmo && (
        <div style={{ fontSize: 12, color: "var(--hi-tinta)", marginBottom: 6 }}>
          <b>{miles(ritmo.al_dia)}</b> al día. <span style={{ color: "var(--hi-tenue)" }}>{deDondeEsElRitmo(ritmo)}</span>
        </div>
      )}
      {sinLeer && <div role="alert" style={{ fontSize: 12, color: "var(--hi-aviso)", marginBottom: 6 }}>{sinLeer}</div>}
      {ritmo && (<>
        <input type="text" inputMode="numeric" autoComplete="off" aria-label="Palabras al día" placeholder={`Por ejemplo, ${miles(ritmo.al_dia)}`}
          value={texto} onChange={(e) => setTexto(e.target.value)} disabled={ocupado}
          style={{ width: "100%", boxSizing: "border-box", padding: 8, borderRadius: 8, background: "var(--hi-papel)", color: "var(--hi-tinta)", border: "1px solid var(--hi-raya-2)", marginBottom: 6 }} />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {conLibro && <button onClick={() => guardar("libro")} disabled={ocupado} style={boton}>Para este libro</button>}
          <button onClick={() => guardar("autor")} disabled={ocupado} style={boton}>{rotuloParaTodos(ritmo)}</button>
          {conLibro && ritmo.libro != null && <button onClick={() => guardar("libro", true)} disabled={ocupado} style={{ ...boton, background: "var(--hi-papel)", color: "var(--hi-suave)" }}>Quitar la de este libro</button>}
        </div>
        <div aria-live="polite" style={{ fontSize: 12, marginTop: 4, color: dicho && !dicho.bien ? "var(--hi-aviso)" : "var(--hi-tinta)" }}>{dicho?.texto}</div>
        {!dir ? <div style={nota}>Para ponerle una cifra a un libro, guarda antes el documento en este ordenador y vuelve a abrir el panel.</div>
          : !ritmo.hay_guion ? <div style={nota}>Para ponerle una cifra a este libro, trae antes su guion (pestaña Guion).</div> : null}
      </>)}
      <div style={nota}>Con ellas el tablero calcula cuánto te falta.</div>
    </div>
  );
}
const boton = { flex: "1 1 auto", padding: "8px 10px", borderRadius: 8, border: "1px solid var(--hi-raya-2)", background: "var(--hi-menu)", color: "var(--hi-tinta)", fontWeight: 700, fontSize: 12, cursor: "pointer" } as const;
const nota = { fontSize: 11, color: "var(--hi-apagado)", marginTop: 4 } as const;
